import { faFloppyDisk, faGamepadModern, faRotateLeft, faWrench, faX, faCopy } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { useState, useSyncExternalStore, useCallback, useEffect } from 'react';
import { gamepadStore } from './store';
import { faKeyboard as faKeyboardDuotone } from '@fortawesome/duotone-light-svg-icons/faKeyboard';
import { faMouse as faMouseDuotone } from '@fortawesome/duotone-light-svg-icons/faMouse';
import constants from '@playcastdotio/GlobalConfig';

export const ButtonMenu = ({
  disconnect,
  statsToggle,
  isMouseEnabled,
  isKeyboardEnabled,
  gamepadSlot,
  timerRemainingSeconds,
  timerTotalSeconds,
  timerPosition = 'bottom',
  optionsDisabled = false,
  afkMode = false,
}: {
  disconnect?: () => void;
  statsToggle?: () => void;
  isMouseEnabled?: boolean;
  isKeyboardEnabled?: boolean;
  gamepadSlot?: number;
  timerRemainingSeconds?: number;
  timerTotalSeconds?: number;
  timerPosition?: 'top' | 'bottom';
  /** When true, the Options button is visible but not interactive (used for ad units) */
  optionsDisabled?: boolean;
  /** When true, timer bar switches to AFK styling (red colors, "AFK" label, pulsing glow) */
  afkMode?: boolean;
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isAdvanceSettingsOpen = useSyncExternalStore(gamepadStore.subscribeAdvanceSettings, gamepadStore.getIsAdvanceSettingsGamepad);

  // Format timer as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleMenuOpen = useCallback(() => {
    if (optionsDisabled) return;
    if (isAdvanceSettingsOpen) return;
    setIsMenuOpen(!isMenuOpen);
  }, [optionsDisabled, isAdvanceSettingsOpen, isMenuOpen]);

  const handleExitStream = useCallback(() => {
    console.log('Exit stream clicked');
    disconnect?.();
    setIsMenuOpen(false);
    if (document.fullscreenElement) document.exitFullscreen();
  }, [disconnect]);

  const handleStatsToggle = useCallback(() => {
    statsToggle?.();
  }, [statsToggle]);

  const handleGamepadOptions = useCallback((value: boolean) => {
    gamepadStore.setIsGamepadCustomizationOpen(value);
    setIsMenuOpen(false);
  }, []);

  const handleResetLayout = useCallback(() => {
    gamepadStore.resetLayout();
    setIsMenuOpen(false);
  }, []);

  const handleAdvanceSettingsToggle = useCallback(() => {
    gamepadStore.setIsAdvanceSettingsGamepad(true);
  }, []);

  const handleCopyLayout = useCallback(async () => {
    try {
      const layout = gamepadStore.getLayout();

      // Validate layout data before formatting
      const isValidLayout = Object.values(layout).every(
        (pos) =>
          pos &&
          typeof pos === 'object' &&
          'x' in pos &&
          'y' in pos &&
          'scale' in pos &&
          typeof pos.x === 'string' &&
          typeof pos.y === 'string' &&
          typeof pos.scale === 'number'
      );

      if (!isValidLayout) {
        console.error('[ButtonMenu] ❌ Invalid layout data:', layout);
        return;
      }

      // Format as valid JSON with "defaultLayout:" prefix for theme.ts compatibility
      // Using JSON format prevents security issues with code injection
      const formattedLines: string[] = ['defaultLayout: {'];

      // Sort keys for consistent output
      const keys = Object.keys(layout).sort() as (keyof typeof layout)[];

      keys.forEach((key, index) => {
        const position = layout[key];
        // Use double quotes for valid JSON format
        const line = `  "${key}": { "x": "${position.x}", "y": "${position.y}", "scale": ${position.scale} }`;
        formattedLines.push(index < keys.length - 1 ? line + ',' : line);
      });

      formattedLines.push('}');

      const formattedOutput = formattedLines.join('\n');

      await navigator.clipboard.writeText(formattedOutput);
    } catch (error) {
      console.error('[ButtonMenu] ❌ Failed to copy layout:', error);
    }
  }, []);

  // Add fullscreen change event listener cleanup
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const isCustomizing = gamepadStore.getIsGamepadCustomizationOpen();

  return (
    <div
      className={clsx(
        'touch-action-none fixed left-1/2 right-auto z-[99999] flex w-fit -translate-x-1/2 items-center gap-2',
        // Passthrough during normal play so empty space around the bar reaches the
        // game (#13832), but capture while customizing so taps in the gaps between
        // toolbar buttons don't fall through to the gamepad shell and close edit mode.
        isCustomizing ? 'pointer-events-auto' : 'pointer-events-none',
        timerPosition === 'top' ? 'top-0 flex-col-reverse' : 'bottom-0 flex-col',
        isCustomizing && (timerPosition === 'top' ? 'mt-2' : 'mb-2'),
      )}
      data-gamepad-input
    >
      {!isCustomizing ? (
        <>
          <button
            type="button"
            className={clsx(
              'pointer-events-auto flex w-[180px] cursor-pointer items-center justify-center gap-1 rounded-lg border border-white bg-black px-2 py-1 text-white transition-all duration-200',
              timerPosition === 'top'
                ? (isMenuOpen ? 'translate-y-[2px]' : 'translate-y-[-200px]')
                : (isMenuOpen ? 'translate-y-[-2px]' : 'translate-y-[200px]'),
            )}
            onClick={() => handleGamepadOptions(true)}
          >
            <FontAwesomeIcon icon={faGamepadModern} className='text-xs' /> <span className='font-[Syne]'>Gamepad Options</span>
          </button>
          <button
            type="button"
            className={clsx(
              'pointer-events-auto flex w-[180px] cursor-pointer items-center justify-center gap-1 rounded-lg border border-white bg-black px-2 py-1 text-white transition-all duration-200',
              timerPosition === 'top'
                ? (isMenuOpen ? 'translate-y-[2px]' : 'translate-y-[-200px]')
                : (isMenuOpen ? 'translate-y-[-2px]' : 'translate-y-[200px]'),
            )}
            onClick={handleStatsToggle}
          >
            <FontAwesomeIcon icon={faGamepadModern} className='text-xs' /> <span className='font-[Syne]'>Stats</span>
          </button>
          <button
            type="button"
            className={clsx(
              'pointer-events-auto flex w-[180px] cursor-pointer items-center justify-center gap-1 rounded-lg border border-white bg-black px-2 py-1 text-white transition-all duration-300',
              timerPosition === 'top'
                ? (isMenuOpen ? 'translate-y-[2px]' : 'translate-y-[-200px]')
                : (isMenuOpen ? 'translate-y-[-2px]' : 'translate-y-[200px]'),
            )}
            onClick={handleExitStream}
          >
            <FontAwesomeIcon icon={faX} className='text-xs' /> <span className='font-[Syne]'>Exit Stream</span>
          </button>

          {timerRemainingSeconds !== undefined && timerTotalSeconds !== undefined ? (
            // Timer-integrated Options button
            <div
              className={clsx(
                `pointer-events-auto relative z-[99999] overflow-hidden ${timerPosition === 'top' ? 'rounded-b-3xl' : 'rounded-t-3xl'}`,
                optionsDisabled && 'pointer-events-none',
              )}
              style={afkMode ? { backgroundColor: '#FCA5A5', animation: 'afk-timer-glow 2.5s ease-in-out infinite' } : undefined}
            >
              {/* Progress bar background */}
              <div
                className='absolute inset-y-0 right-0 transition-all duration-1000 ease-linear'
                style={{
                  backgroundColor: afkMode ? '#991B1B' : '#4338CA',
                  width: `${Math.max(0, Math.min(100, (timerRemainingSeconds / timerTotalSeconds) * 100))}%`,
                }}
              />

              {/* Dark overlay (non-AFK only; AFK uses explicit container bg) */}
              {!afkMode && (
                <div
                  className='absolute inset-0'
                  style={{ backgroundColor: 'rgba(49, 46, 129, 0.5)' }}
                />
              )}

              {/* Content */}
              <div
                className='relative flex h-10 items-center gap-3 bg-transparent px-3 py-0.5 font-[Inter] font-semibold text-white'
              >
                <span
                  className='text-lg font-bold tabular-nums'
                >
                  {afkMode ? `AFK | ${formatTime(timerRemainingSeconds)}` : formatTime(timerRemainingSeconds)}
                </span>
                {!optionsDisabled && !afkMode && (
                  <>
                    <div className='h-4 w-px bg-white opacity-30' />
                    <button type="button" className='flex items-center gap-1.5' onClick={handleMenuOpen}>
                      <span className='text-xs font-medium'>Options</span>
                    </button>
                  </>
                )}
              </div>

              {afkMode && (
                <style>{`
                  @keyframes afk-timer-glow {
                    0%, 100% { box-shadow: 0 0 8px 2px rgba(220, 38, 38, 0.4); }
                    50% { box-shadow: 0 0 18px 6px rgba(220, 38, 38, 0.8); }
                  }
                `}</style>
              )}
            </div>
          ) : (
            // Original Options button (no timer)
            <button
              type="button"
              className={clsx(
                `pointer-events-auto z-[99999] flex h-10 items-center gap-2 ${timerPosition === 'top' ? 'rounded-b-lg border-x-2 border-b-2' : 'rounded-t-lg border-x-2 border-t-2'} border-white bg-black px-2 py-0.5 font-[Syne] font-semibold text-white`,
                optionsDisabled && 'pointer-events-none opacity-50',
              )}
              onClick={handleMenuOpen}
              disabled={optionsDisabled}
            >
              {gamepadSlot !== undefined && gamepadSlot !== -1 && <span className='font-[Syne] text-lg'>P{gamepadSlot + 1}</span>}
              {isKeyboardEnabled || isMouseEnabled ? (
                <div className='relative flex items-center'>
                  {isKeyboardEnabled && (
                    <FontAwesomeIcon
                      icon={faKeyboardDuotone}
                      className={clsx('', 'size-7')}
                      style={
                        {
                          '--fa-secondary-color': '#000000',
                          '--fa-secondary-opacity': '1',
                        } as React.CSSProperties
                      }
                    />
                  )}
                  {isMouseEnabled && (
                    <FontAwesomeIcon
                      icon={faMouseDuotone}
                      className={clsx(isKeyboardEnabled ? 'absolute -right-1 bottom-0 size-4' : 'size-7')}
                      style={
                        {
                          '--fa-secondary-color': '#000000',
                          '--fa-secondary-opacity': '1',
                        } as React.CSSProperties
                      }
                    />
                  )}
                </div>
              ) : (
                ''
              )}
              <span className='text-sm'>Options</span>
            </button>
          )}
        </>
      ) : (
        <div className={clsx('flex gap-2', timerPosition === 'top' ? 'mt-2.5' : 'mb-2.5')}>
          <button
            type="button"
            className={clsx(
              'pointer-events-auto flex cursor-pointer items-center justify-center gap-1 rounded-lg border border-white bg-black px-2 py-1 text-white transition-all duration-300',
            )}
            onClick={handleAdvanceSettingsToggle}
            onTouchStart={(e) => {
              e.stopPropagation(); // Prevent touch from reaching draggable joysticks below
            }}
            onTouchEnd={(e) => {
              e.preventDefault(); // Prevent mouse event emulation
              e.stopPropagation();
              handleAdvanceSettingsToggle();
            }}
          >
            <FontAwesomeIcon icon={faWrench} className='text-xs' /> <span className='font-[Syne]'>Advanced Options</span>
          </button>
          <button
            type="button"
            className={clsx(
              'pointer-events-auto flex cursor-pointer items-center justify-center gap-1 rounded-lg border border-white bg-black px-2 py-1 text-white transition-all duration-300',
            )}
            onClick={handleResetLayout}
            onTouchStart={(e) => {
              e.stopPropagation(); // Prevent touch from reaching draggable joysticks below
            }}
            onTouchEnd={(e) => {
              e.preventDefault(); // Prevent mouse event emulation
              e.stopPropagation();
              handleResetLayout();
            }}
          >
            <FontAwesomeIcon icon={faRotateLeft} className='text-xs' /> <span className='font-[Syne]'>Reset Layout</span>
          </button>
          {constants.BRANCH !== 'prerelease' && constants.BRANCH !== 'release' && (
            <button
              type="button"
              className={clsx(
                'pointer-events-auto flex cursor-pointer items-center justify-center gap-1 rounded-lg border border-blue-500 bg-blue-900/30 px-2 py-1 text-blue-300 transition-all duration-300',
              )}
              onClick={handleCopyLayout}
              onTouchStart={(e) => {
                e.stopPropagation(); // Prevent touch from reaching draggable joysticks below
              }}
              onTouchEnd={(e) => {
                e.preventDefault(); // Prevent mouse event emulation
                e.stopPropagation();
                handleCopyLayout();
              }}
            >
              <FontAwesomeIcon icon={faCopy} className='text-xs' /> <span className='font-[Syne]'>Copy JSON</span>
            </button>
          )}
          <button
            type="button"
            className={clsx(
              'pointer-events-auto flex cursor-pointer items-center justify-center gap-1 rounded-lg border border-white bg-black px-2 py-1 text-white transition-all duration-300',
            )}
            onClick={() => handleGamepadOptions(false)}
            onTouchStart={(e) => {
              e.stopPropagation(); // Prevent touch from reaching draggable joysticks below
            }}
            onTouchEnd={(e) => {
              e.preventDefault(); // Prevent mouse event emulation
              e.stopPropagation();
              handleGamepadOptions(false);
            }}
          >
            <FontAwesomeIcon icon={faFloppyDisk} className='text-xs' /> <span className='font-[Syne]'>Save Layout</span>
          </button>
        </div>
      )}
    </div>
  );
};
